import {
  DestroyRef,
  DOCUMENT,
  inject,
  Injectable,
  InjectionToken,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  TitleStrategy,
} from '@angular/router';
import {
  MatomoInitializerService,
  MatomoTracker,
} from 'ngx-matomo-client';
import {filter} from 'rxjs';
import {environment} from '../../../../../../environments/environment';
import {Constants} from '../../../../common/constants/constants';
import {isMatomoConfigured} from '../../../../common/functions/matomo';
import {IMatomoConfig} from '../../../../models/matomo.model';
import {UserAclService} from '../../auth/acl/user-acl/user-acl.service';
import {JwtService} from '../../auth/jwt/jwt.service';
import {PublicEntrypointService} from '../../auth/public-entrypoint/public-entrypoint.service';
import {IframeService} from '../../iframe/iframe.service';
import {SharedLinkService} from '../../sharing/shared-link.service';

/**
 * Constant: Matomo tracker the page views are sent to, from the environment
 */
export const MATOMO_CONFIG: InjectionToken<IMatomoConfig> = new InjectionToken<IMatomoConfig>('MATOMO_CONFIG', {
  providedIn: 'root',
  factory: (): IMatomoConfig => environment.matomo,
});

@Injectable({
  providedIn: 'root',
})
export class MatomoService {

  private readonly config: IMatomoConfig = inject(MATOMO_CONFIG);

  private readonly router: Router = inject(Router);

  private readonly titleStrategy: TitleStrategy = inject(TitleStrategy);

  private readonly document: Document = inject(DOCUMENT);

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly tracker: MatomoTracker = inject(MatomoTracker);

  private readonly matomoInitializer: MatomoInitializerService = inject(MatomoInitializerService);

  private readonly jwtService: JwtService = inject(JwtService);

  private readonly userAclService: UserAclService = inject(UserAclService);

  private readonly iframeService: IframeService = inject(IframeService);

  private readonly sharedLinkService: SharedLinkService = inject(SharedLinkService);

  private readonly publicEntrypointService: PublicEntrypointService = inject(PublicEntrypointService);

  private isTrackerInitialized = false;

  private previousUrl: string | null = null;

  /**
   * Method: Track a page view after every navigation, where the environment names a Matomo tracker
   */
  public startPageViewTracking(): void {
    if (!isMatomoConfigured(this.config)) {
      return;
    }
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.trackPageView());
  }

  /**
   * Method: Send the route pattern and route title key to Matomo, for a logged-in user only
   */
  private trackPageView(): void {
    if (!this.isTrackedVisitor()) {
      return;
    }
    const snapshot = this.router.routerState.snapshot;
    const url = this.document.location.origin + this.buildRoutePattern(snapshot.root);
    this.initializeTracker();
    this.tracker.setReferrerUrl(this.previousUrl ?? this.document.location.origin);
    this.tracker.setCustomUrl(url);
    this.tracker.setDocumentTitle(this.titleStrategy.buildTitle(snapshot) ?? '');
    this.tracker.trackPageView();
    this.previousUrl = url;
  }

  /**
   * Method: Build the matched route path with parameter names instead of values
   * @param root - Root of the activated route tree
   */
  private buildRoutePattern(root: ActivatedRouteSnapshot): string {
    const segments: string[] = [];
    let route: ActivatedRouteSnapshot | null = root;
    while (route) {
      if (route.routeConfig?.path) {
        segments.push(route.routeConfig.path);
      }
      route = route.firstChild;
    }
    return Constants.SLASH + segments.join(Constants.SLASH);
  }

  /**
   * Method: Check the visitor is a logged-in user, not a shared-link guest, the iframe or a public entry point
   */
  private isTrackedVisitor(): boolean {
    return this.jwtService.hasAccessToken()
      && !this.userAclService.isGuestRole()
      && !this.iframeService.isIframePage()
      && !this.sharedLinkService.isSharedLinkPage()
      && !this.publicEntrypointService.isPublicEntryPointPage();
  }

  /**
   * Method: Set the cookie domain and load the tracker once
   */
  private initializeTracker(): void {
    if (this.isTrackerInitialized) {
      return;
    }
    this.isTrackerInitialized = true;
    this.tracker.setCookieDomain(this.config.cookieDomain);
    this.matomoInitializer.initializeTracker({trackerUrl: this.config.trackerUrl, siteId: this.config.siteId});
  }

}
